/**
 * Iter 7 hardening (Enterprise Projects Phase 1) — deterministic date
 * formatters.
 *
 * Extracted into a pure-JS module so:
 *   * The formatters have zero JSX / React coupling and can be unit-
 *     tested with plain Node.js (no jest transform required).
 *   * Every surface (Projects List, Project Detail, Work Item Detail,
 *     Workspaces Panel, Accountability Chain, My Work) imports the
 *     SAME implementation.
 *
 * ## Canonical formats
 *
 *   * ``formatDate(iso)`` → ``DD MMM YYYY``     e.g. "24 Jul 2026"
 *   * ``formatDateTime(iso)`` → ``DD MMM YYYY, hh:mm AM/PM``
 *                                                e.g. "24 Jul 2026, 10:30 AM"
 *
 * Output is **identical across every browser / operating system**
 * because we do NOT call ``toLocaleDateString`` (which is locale-
 * dependent). Timezone-local (matches the browser's current time
 * zone) — REEDOS BCMS does not yet define an organisation-wide
 * timezone, and this iteration explicitly must not introduce one.
 *
 * All three formatters are safe on ``null`` / ``undefined`` /
 * malformed inputs. Empty input renders "—". Non-parsable strings
 * are returned verbatim so upstream diagnostics remain visible.
 */

// Private month table — locked to en-GB abbreviations so the output
// is byte-identical for every user regardless of OS locale.
const MONTHS_ABBR = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];


/** Return true iff the value is a Date whose time is a valid number. */
function _isValidDate(d) {
    return d instanceof Date && !Number.isNaN(d.getTime());
}


export function formatDate(iso) {
    if (iso === null || iso === undefined || iso === "") return "—";
    try {
        // "2026-07-24" (10 chars) → parse as LOCAL midnight so it doesn't
        // silently shift to the previous day in negative timezones.
        const raw = String(iso).length <= 10 ? `${iso}T00:00:00` : iso;
        const d = new Date(raw);
        if (!_isValidDate(d)) return String(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mmm = MONTHS_ABBR[d.getMonth()];
        const yyyy = d.getFullYear();
        return `${dd} ${mmm} ${yyyy}`;
    } catch {
        return String(iso);
    }
}


export function formatDateTime(iso) {
    if (iso === null || iso === undefined || iso === "") return "—";
    try {
        const d = new Date(iso);
        if (!_isValidDate(d)) return String(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mmm = MONTHS_ABBR[d.getMonth()];
        const yyyy = d.getFullYear();
        const h24 = d.getHours();
        const ampm = h24 >= 12 ? "PM" : "AM";
        const h12 = h24 % 12 || 12;
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${dd} ${mmm} ${yyyy}, ${String(h12).padStart(2, "0")}:${mm} ${ampm}`;
    } catch {
        return String(iso);
    }
}


/**
 * Render a user by name preference: name → email → user_id.
 * Accepts either a snapshot object or a bare id string.
 */
export function formatUserName(snapshotOrId, fallbackId) {
    if (!snapshotOrId && !fallbackId) return "—";
    if (typeof snapshotOrId === "string") return snapshotOrId;
    if (snapshotOrId && typeof snapshotOrId === "object") {
        return snapshotOrId.name
            || snapshotOrId.email
            || fallbackId
            || snapshotOrId.user_id
            || "—";
    }
    return fallbackId || "—";
}
